import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './AddPGDetails.css';
import tenantStorage from '../../utils/tenantStorage';
import { apiPost } from '../../api/apiClient';
import { toastError, toastSuccess } from '../common/toast';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import '../common/rtCommon.css';
import RTPageLayout from '../common/RTPageLayout';

const initialState = {
  name: '',
  dob: null,
  gender: '',
  aadhar: '',
  student: false,
  working: false,
  food: 'yes',
  mobile: '',
  whatsapp: false,
  email: '',
  parentMobile: '',
  address: '',
};

const AddPGDetails = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const mode = location.state?.mode || props.mode || 'add';
  const pgId = location.state?.pgId || props.pgId || null;
  const initialData = location.state?.initialData || props.initialData || null;

  // Extract callbacks from props
  const onSuccess = props.onSuccess;
  const onCancel = props.onCancel;

  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const isDigits = (val) => /^\d*$/.test(val);
  const isMobile = (val) => /^\d{10}$/.test(val);
  const isAadhar = (val) => val === '' || /^\d{12}$/.test(val);
  const isEmail = (val) => val === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    // DOB is now optional - removed validation
    if (!form.gender) newErrors.gender = 'Gender is required';
    if (!form.mobile) newErrors.mobile = 'Mobile number is required';
    // Address is now optional - removed validation
    if (!form.food) newErrors.food = 'Food selection is required';
    if (form.mobile && !/^\d{10}$/.test(form.mobile))
      newErrors.mobile = 'Enter a valid 10-digit mobile number';
    if (form.parentMobile && !/^\d{10}$/.test(form.parentMobile))
      newErrors.parentMobile = 'Enter a valid 10-digit mobile number';
    if (form.aadhar && !/^\d{12}$/.test(form.aadhar))
      newErrors.aadhar = 'Aadhar must be 12 digits';
    if (form.aadhar && /\D/.test(form.aadhar))
      newErrors.aadhar = 'Aadhar must contain only digits';
    if (form.mobile && /\D/.test(form.mobile))
      newErrors.mobile = 'Mobile must contain only digits';
    if (form.parentMobile && /\D/.test(form.parentMobile))
      newErrors.parentMobile = 'Mobile must contain only digits';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      newErrors.email = 'Enter a valid email address';
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleRadioChange = (e) => {
    setForm((prev) => ({ ...prev, food: e.target.value }));
    setErrors((prev) => ({ ...prev, food: undefined }));
  };

  const handleDateChange = (date) => {
    setForm((prev) => ({ ...prev, dob: date }));
    setErrors((prev) => ({ ...prev, dob: undefined }));
  };

  const handleDigitsInput = (e) => {
    const { name, value } = e.target;
    const newValue = value.replace(/\D/g, '').slice(0, 10);
    setForm((prev) => ({ ...prev, [name]: newValue }));
  };

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setForm({
        ...initialData,
        dob: initialData.dob ? new Date(initialData.dob) : null,
      });
    }
  }, [mode, initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      setIsSaving(true);
      try {
        const token = tenantStorage.getItem('token');
        const payload = {
          ...form,
          dob: form.dob ? form.dob.toISOString().split('T')[0] : null,
          food: form.food === 'yes' ? true : false,
          parent_mobile:
            form.parentMobile && form.parentMobile !== ''
              ? form.parentMobile
              : null,
          aadhar: form.aadhar && form.aadhar !== '' ? form.aadhar : null,
          email: form.email && form.email !== '' ? form.email : null,
        };
        if (mode === 'edit' && pgId) payload.pg_id = pgId;
        const response = await apiPost('/pgdetails', payload, {
          Authorization: `Bearer ${token}`,
        });
        if (response.status === 200 && response.data.status === 'Success') {
          toastSuccess('Paying Guest details saved successfully');
          setForm(initialState);

          // If onSuccess callback is provided (from SelectPG), call it with the new PG data
          if (onSuccess) {
            // Create PG object with the response data and form data
            const newPG = {
              pg_id: response.data.pg_id,
              name: form.name,
              mobile: form.mobile,
              address: form.address,
              ...response.data,
            };
            onSuccess(newPG);
          } else {
            // Default behavior - navigate to PG details view
            navigate('/paying-guest/view/' + response.data.pg_id);
          }
        } else {
          toastError('Error saving details');
        }
      } catch (err) {
        toastError('Error saving details' + err.response);
      } finally {
        setIsSaving(false);
      }
    }
  };

  return (
    <RTPageLayout
      activePath={location.pathname}
      title={
        <>
          {mode === 'edit'
            ? 'Edit Paying Guest Details'
            : 'Add Paying Guest Details'}
        </>
      }
      actions={
        <>
          {mode !== 'edit' && (
            <RTButton
              variant="secondary"
              onClick={() => (onCancel ? onCancel() : navigate(-1))}
            >
              &#8592; Back
            </RTButton>
          )}
          {mode === 'edit' && (
            <RTButton
              type="button"
              variant="secondary"
              onClick={() => (onCancel ? onCancel() : navigate(-1))}
            >
              Cancel
            </RTButton>
          )}
          {/* Add other action buttons if needed */}
        </>
      }
      mainSection={true}
    >
      <form className="add-pg-form" onSubmit={handleSubmit} noValidate>
        <div className="add-pg-sections">
          <div className="rt-section">
            <h3 className="rt-section-title">Personal Details</h3>
            <div className="form-group">
              <label>
                Name <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
              />
              {errors.name && <span className="form-error">{errors.name}</span>}
            </div>
            <div className="form-group">
              <label>Date of Birth</label>
              <DatePicker
                selected={form.dob}
                onChange={handleDateChange}
                dateFormat="dd/MM/yyyy"
                placeholderText="DD/MM/YYYY"
                className="custom-datepicker"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                maxDate={new Date()}
                isClearable
                autoComplete="off"
              />
              {errors.dob && <span className="form-error">{errors.dob}</span>}
            </div>
            <div className="form-group">
              <label>
                Gender <span style={{ color: 'red' }}>*</span>
              </label>
              <select name="gender" value={form.gender} onChange={handleChange}>
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
              {errors.gender && (
                <span className="form-error">{errors.gender}</span>
              )}
            </div>
            <div className="form-group">
              <label>Aadhar Number</label>
              <input
                type="text"
                name="aadhar"
                maxLength={12}
                value={form.aadhar}
                onChange={handleChange}
                onInput={handleDigitsInput}
                inputMode="numeric"
              />
              {errors.aadhar && (
                <span className="form-error">{errors.aadhar}</span>
              )}
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="student"
                  checked={form.student}
                  onChange={handleChange}
                />
                Student
              </label>
              <label>
                <input
                  type="checkbox"
                  name="working"
                  checked={form.working}
                  onChange={handleChange}
                />
                Working
              </label>
            </div>
            <div className="form-group">
              <label>
                Food <span style={{ color: 'red' }}>*</span>
              </label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name="food"
                    value="yes"
                    checked={form.food === 'yes' || form.food === true}
                    onChange={handleRadioChange}
                  />{' '}
                  Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name="food"
                    value="no"
                    checked={form.food === 'no' || form.food === false}
                    onChange={handleRadioChange}
                  />{' '}
                  No
                </label>
              </div>
              {errors.food && <span className="form-error">{errors.food}</span>}
            </div>
          </div>
          <div className="rt-section">
            <h3 className="rt-section-title">Communication Details</h3>
            <div className="form-group">
              <label>
                Mobile Number <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                name="mobile"
                maxLength={10}
                value={form.mobile}
                onChange={handleChange}
                onInput={handleDigitsInput}
                inputMode="numeric"
              />
              {errors.mobile && (
                <span className="form-error">{errors.mobile}</span>
              )}
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="whatsapp"
                  checked={form.whatsapp}
                  onChange={handleChange}
                />
                Whatsapp
              </label>
            </div>
            <div className="form-group">
              <label>Address</label>
              <textarea
                name="address"
                rows={3}
                value={form.address}
                onChange={handleChange}
              />
              {errors.address && (
                <span className="form-error">{errors.address}</span>
              )}
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
              />
              {errors.email && (
                <span className="form-error">{errors.email}</span>
              )}
            </div>
            <div className="form-group">
              <label>Parent/Guardian Mobile Number</label>
              <input
                type="text"
                name="parentMobile"
                maxLength={10}
                value={form.parentMobile}
                onChange={handleChange}
                onInput={handleDigitsInput}
                inputMode="numeric"
              />
              {errors.parentMobile && (
                <span className="form-error">{errors.parentMobile}</span>
              )}
            </div>
          </div>
        </div>
        <div className="add-pg-actions">
          <RTButton
            type="submit"
            variant="primary"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <RTSpinner
                  size="small"
                  showMessage={false}
                  style={{ marginRight: '8px' }}
                />
              </>
            ) : (
              'Save'
            )}
          </RTButton>
        </div>
      </form>
    </RTPageLayout>
  );
};

export default AddPGDetails;
